import { cx } from '@flowus/common/cx';
import { PermissionRole } from '@next-space/fe-api-idl';
import type { Timeout } from 'ahooks/lib/useRequest/src/types';
import type { FC } from 'react';
import { useEffect, useMemo, useRef } from 'react';
import * as Empty from 'src/components/empty';
import { ToSharedPage } from 'src/components/empty/to-shared-page';
import { ServiceCenter } from 'src/components/service-center';
import { useBlockLocked } from 'src/hooks/block/use-block-locked';
import { useScheduleSyncBlock } from 'src/hooks/block/use-schedule-sync-block';
import { useFetchSpaceCapacity } from 'src/hooks/drive/use-fetch-space-capacity';
import { useFetchRootPages } from 'src/hooks/page';
import { useAutoChangeDomain } from 'src/hooks/page/use-auto-change-domain';
import { checkUrl } from 'src/hooks/page/use-check-url';
import { useCopyListener } from 'src/hooks/page/use-copy-listener';
import { useDnd } from 'src/hooks/page/use-dnd';
import { useFetchAiConfig } from 'src/hooks/page/use-fetch-ai-config';
import { useFetchSharePages } from 'src/hooks/page/use-fetch-share-pages';
import { useMouseEvent } from 'src/hooks/page/use-mouse-event';
import { usePageMeta } from 'src/hooks/page/use-page-meta';
import { usePageShortcut } from 'src/hooks/page/use-page-shortcut';
import { useSendMembersState } from 'src/hooks/public/use-listen-members-state';
import { PermissionsProvider, usePermissions } from 'src/hooks/share/use-permissions';
import { useAutoSwitchSpace, useCurrentSpace, useCurrentSpaceDomain } from 'src/hooks/space';
import { useOpenDialogFromLocation } from 'src/hooks/space/use-open-dialog-from-location';
import { useOpenWatermark } from 'src/hooks/space/use-open-watermark';
import { fetchKolUserIds } from 'src/hooks/template/use-fetch-templates';
import { cache } from 'src/redux/store';
import {
  setAppUiState,
  useFirstLoadPage,
  useGetOffline,
  useIsMobileSize,
} from 'src/services/app/hook';
import { fetchSubscriptionData } from 'src/services/app/hook/subscription-data';
import { fetchAiCapacity } from 'src/services/capacity';
import { fetchCurrentSpacePlans } from 'src/services/capacity/space-plans';
import { useObservableBlock } from 'src/services/rxjs-redux/hook';
import { useGetSyncService } from 'src/services/sync';
import { useUploadInfoSize } from 'src/services/upload';
import { fetchUserBalance } from 'src/services/user/user-balance';
import { $searchParams, ViewPath } from 'src/utils';
import { bizTracker } from 'src/utils/biz-tracker';
import { isCollection } from 'src/utils/block-type-utils';
import { judgeSharePage, useCheckChatPage, useGetPageId } from 'src/utils/getPageId';
import { monitorMouseFocusInElements } from 'src/utils/monitor-mouse-in-elements';
import { useGetRightPageId } from 'src/utils/right-utils';
import { useSyncUpFaultModal } from '../../hooks/public/useSyncUpFaultModal';
import { ChatPage } from '../chat-page';
import { PageDocEmpty } from '../empty/page-doc-empty';
import { PageOfflineEmpty } from '../empty/page-offline-empty';
import { Aside } from './aside';
import { Header } from './header';
import { Layout } from './layout';
import { MainContent } from './main-content';
import { RightPage, RightPageOut } from './right-page';

export const Main: FC = () => {
  const firstLoadPage = useFirstLoadPage();
  const currentSpace = useCurrentSpace();
  const hasSpace = Boolean(currentSpace.uuid);
  const fetchSpaceRootPages = useFetchRootPages();
  const fetchSpaceSharePages = useFetchSharePages();
  const offline = useGetOffline();
  const pageId = useGetPageId();
  const autoSwitchSpace = useAutoSwitchSpace();
  const domain = useCurrentSpaceDomain();
  const isInValidUrl = checkUrl(pageId, domain);
  const pageMeta = usePageMeta(pageId);
  const { roleWithoutPublic, shared } = usePermissions(pageId);

  const sync = useGetSyncService();
  useOpenDialogFromLocation();

  // const [asideWidth, _] = useLocalStorageState(LocalStorageKey.MAIN_LEFT_WIDTH, {
  //   defaultValue: defaultAsideWidth,
  // });
  // const leftWidth = Math.min(Math.max(200, asideWidth ?? 0), window.innerWidth * 0.7);
  // const lastRightId = useRef<string>();
  // const toggleCollapse = useToggleCollapse();

  // useSizeCallback(
  //   mainDivRef.current,
  //   (ret) => {
  //     const storageCollapsed = localStorage.getItem(TOC_COLLAPSED) === 'true';
  //     if (!ret.width) return;
  //     if (storageCollapsed) return;
  //     const rightPageId = parseRightPageId(location.search);
  //     const isOpenRightPanel = !lastRightId.current && rightPageId;
  //     lastRightId.current = rightPageId;

  //     // 由于收起之后宽度会一下子变宽，因此不能用COLLAPSED_WIDTH，而应该用COLLAPSED_WIDTH+leftWidth来进行对比
  //     if (isOpenRightPanel && !$appUiStateCache.$isCollapsed && ret.width <= COLLAPSED_WIDTH) {
  //       toggleCollapse(true);
  //     } else if ($appUiStateCache.$isCollapsed && ret.width > COLLAPSED_WIDTH + leftWidth) {
  //       toggleCollapse(false);
  //     }
  //   },
  //   'width'
  // );

  useEffect(() => {
    if (!hasSpace) return;
    if (!pageId) return;
    if (pageMeta?.tag) return;
    if (location.pathname.includes(ViewPath.chat)) return;
    void autoSwitchSpace(pageId);
  }, [autoSwitchSpace, pageId, hasSpace, pageMeta?.tag]);

  const scheduleUpdateBlock = useScheduleSyncBlock();

  useEffect(() => {
    if (!currentSpace.uuid) return;
    return sync.subscribeChildPages(currentSpace.uuid, null, (packet) => {
      if (packet?.table === 'block' && packet.data.id) {
        scheduleUpdateBlock(packet.data.id);
      }

      if (!packet || packet.table === 'space') {
        void fetchSpaceRootPages(currentSpace.uuid);
      }
    });
  }, [sync, currentSpace.uuid, fetchSpaceRootPages, scheduleUpdateBlock]);

  useEffect(() => {
    if (!currentSpace.uuid) return;
    return sync.subscribeSharedPages(currentSpace.uuid, () => {
      void fetchSpaceSharePages(currentSpace.uuid);
    });
  }, [sync, currentSpace.uuid, fetchSpaceSharePages]);

  useEffect(() => {
    void fetchKolUserIds();
  }, []);

  useEffect(() => {
    const { blocks } = cache;
    if (pageMeta?.tag === 'NOT_FOUND' || pageMeta?.tag === 'NO_PERMISSION') {
      bizTracker.event('page_enter', {
        is_success: false,
        page_type: blocks[pageId]?.type,
        fail_reason: pageMeta.tag === 'NOT_FOUND' ? '已被彻底删除' : '没有访问权限',
      });
    }
  }, [pageId, pageMeta?.tag]);

  // #region 定时更新空间容量
  const capacityTimer = useRef<Timeout>();
  const uploadLen = useUploadInfoSize();
  const fetchSpaceCapacity = useFetchSpaceCapacity();

  useEffect(() => {
    void fetchUserBalance();
    clearInterval(capacityTimer.current);

    const onUpdate = () => {
      if (!currentSpace.uuid) return;
      void fetchSpaceCapacity();
      void fetchSubscriptionData(currentSpace.uuid);
      void fetchAiCapacity(currentSpace.uuid);
    };

    onUpdate();

    // 10分钟同步一次
    capacityTimer.current = setInterval(onUpdate, 60 * 1000 * 15);

    return () => clearInterval(capacityTimer.current);
  }, [currentSpace.uuid, fetchSpaceCapacity, uploadLen]);

  useEffect(() => {
    void fetchCurrentSpacePlans(currentSpace.uuid);
  }, [currentSpace.uuid]);
  // #endregion

  const aside = useMemo(() => {
    if (pageMeta?.tag === 'NO_PERMISSION' || $searchParams.print || $searchParams.embed) {
      return null;
    }
    return <Aside />;
  }, [pageMeta?.tag]);

  // 如果当前没有浏览权限并且此页面开启了分享，寻味是否要去share
  if ((pageMeta?.tag === 'NO_PERMISSION' || roleWithoutPublic === PermissionRole.NONE) && shared) {
    return (
      <>
        <Header />
        <ToSharedPage />
      </>
    );
  }

  // 初始化离线
  if (offline && firstLoadPage) {
    return (
      <>
        <Header />
        <PageOfflineEmpty />
      </>
    );
  }

  if (isInValidUrl) {
    return (
      <>
        <Header />
        <Empty.NotFound />
      </>
    );
  }

  return (
    <Layout aside={aside}>
      <ContentDiversion pageId={pageId} />
    </Layout>
  );
};

const ContentDiversion = (props: { pageId?: string }) => {
  const { pageId } = props;
  const isChatPage = useCheckChatPage();

  if (isChatPage) {
    return <ChatPage id={pageId} />;
  }

  if (pageId) {
    return (
      <PermissionsProvider uuid={pageId}>
        <Content pageId={pageId} />
      </PermissionsProvider>
    );
  }

  return (
    <>
      <Header />
      <PageDocEmpty />
      <ServiceCenter />
    </>
  );
};

const Content: FC<{ pageId: string }> = (props) => {
  const { pageId } = props;
  const isMobileSize = useIsMobileSize();
  const rightPageId = useGetRightPageId();
  const mainDivRef = useRef<HTMLDivElement>(null);
  const rightDivRef = useRef<HTMLDivElement>(null);
  const initRightPage = useRef(!!rightPageId);
  const locked = useBlockLocked(pageId);
  const blockType = useObservableBlock(pageId, (block) => block?.type);

  useDnd();
  useCopyListener(judgeSharePage());
  useSendMembersState();
  useMouseEvent(!isCollection(blockType) ? locked : false);
  usePageShortcut();
  useAutoChangeDomain();
  useSyncUpFaultModal();
  useFetchAiConfig();
  useOpenWatermark();

  const hasRightPanel = Boolean(rightPageId);
  useEffect(() => {
    if (!hasRightPanel) {
      // 如果没有右边的panel就重置下面2个变量

      setAppUiState({ $isFocusInRightPanel: false });
      return;
    }
    initRightPage.current = true;
    // 只有展示右边的时候才监听,监听鼠标移动到哪个panel以及点击到哪个panel
    return monitorMouseFocusInElements(
      {
        onMouseFocusInElement: (container) => {
          setAppUiState({ $isFocusInRightPanel: container === rightDivRef });
        },
      },
      mainDivRef,
      rightDivRef
    );
  }, [hasRightPanel]);

  return (
    <div
      className={cx(
        !$searchParams.print &&
          'relative flex flex-1 h-full min-h-0 w-full overflow-x-hidden animate-fade-in'
      )}
    >
      <div
        ref={mainDivRef}
        key={pageId}
        className={cx(
          'main-panel w-full overflow-hidden',
          !$searchParams.print && 'flex flex-col flex-1 h-full min-h-0',
          isMobileSize ? 'min-w-0' : 'min-w-[200px]'
        )}
      >
        <MainContent uuid={pageId} />
      </div>
      {rightPageId ? (
        <PermissionsProvider uuid={pageId}>
          <RightPage uuid={rightPageId} rightDivRef={rightDivRef}>
            <MainContent uuid={rightPageId} />
          </RightPage>
        </PermissionsProvider>
      ) : (
        initRightPage.current && <RightPageOut />
      )}
    </div>
  );
};
